import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import YouTubeEmbed from '../components/public/YouTubeEmbed';

const EventsPage = ({ user }) => {
  // API Configuration
  const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pastLoading, setPastLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    eventType: '',
    category: '',
    language: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    if (showPastEvents) {
      fetchPastEvents();
    } else {
      fetchEvents();
    }
    checkMemberStatus();
  }, [filters, pagination.page, showPastEvents]);

  const checkMemberStatus = () => {
    const token = localStorage.getItem('userToken') || localStorage.getItem('token');
    setIsMember(!!token && !!user);
  };

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowEventDetails(true);
  };

  const handleRegisterClick = (event) => {
    // Members-only events require login; open events accept everyone
    if (event.members_only && !isMember) {
      toast.warning('This event is for ARCA members only. Please sign in to register.', {
        position: 'top-center',
        autoClose: 5000
      });
      return;
    }

    // Multi-step registration flow: Form -> CPD (conditional) -> Review -> Payment -> Confirmation
    navigate(`/events/${event.id}/register`);
  };

  const handleRegisterFromDetails = () => {
    if (!selectedEvent) return;
    handleRegisterClick(selectedEvent);
  };

  const processRegistration = async () => {
    try {
      setRegistering(true);
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');

      const response = await fetch(`${API_URL}/events/${selectedEvent.id}/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Successfully registered for event!', {
          position: 'top-center',
          autoClose: 3000
        });
        setShowRegistrationForm(false);
        setSelectedEvent(null);
      } else {
        toast.error(data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Failed to register for event');
    } finally {
      setRegistering(false);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      }).toString();

      const response = await fetch(`${API_URL}/events?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setEvents(data.data.events);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages
        }));
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPastEvents = async () => {
    try {
      setPastLoading(true);
      const queryParams = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      }).toString();

      const response = await fetch(`${API_URL}/events/past?${queryParams}`);
      const data = await response.json();

      if (data.success) {
        setPastEvents(data.data.events);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages
        }));
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to fetch past events');
      console.error('Error fetching past events:', err);
    } finally {
      setPastLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    // Convert eventType to lowercase to match database enum values
    const processedValue = filterName === 'eventType' ? value.toLowerCase() : value;
    setFilters(prev => ({
      ...prev,
      [filterName]: processedValue
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filters change
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/New_York' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const est = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' });
    const cst = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' });
    const gmt = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    return `${est} EST / ${cst} CST / ${gmt} GMT`;
  };

  if (loading && !showPastEvents) {
    return (
      <PublicLayout user={user}>
        <div className="min-h-screen d-flex items-center justify-center" style={{ minHeight: '80vh', backgroundColor: '#ffffff' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#6B8E23' }}></div>
          <span className="ml-3" style={{ color: '#4A4F4F' }}>Loading events...</span>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout user={user}>
      <SEO
        title={seoConfig.events.title}
        description={seoConfig.events.description}
        keywords={seoConfig.events.keywords}
        url={seoConfig.events.url}
      />
      {/* ARCA Events Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #6B8E23 0%, #4A6B18 50%, #2C5234 100%)',
        padding: '50px 20px 40px',
        color: '#ffffff',
        textAlign: 'center'
      }}>
        <div className="container">
          <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
            style={{ width: '60px', height: '60px', backgroundColor: 'rgba(255,255,255,0.2)' }}>
            <i className="fas fa-calendar-alt text-white" style={{ fontSize: '28px' }}></i>
          </div>
          <h1 className="fw-bold mb-2" style={{ fontSize: '2.2rem', letterSpacing: '0.5px' }}>ARCA Events & Webinars</h1>
          <p style={{ fontSize: '1.1rem', opacity: 0.9, maxWidth: '600px', margin: '0 auto' }}>
            Join our events to enhance your respiratory care knowledge and connect with professionals across Africa.
          </p>
        </div>
      </div>

      <div className="container-fluid px-3 px-md-4 px-lg-5 py-4" style={{ backgroundColor: '#ffffff', minHeight: '80vh' }}>
        {/* Toggle Button for past Events */}
        <div className="row justify-content-center mb-4">
          <div className="col-auto">
            <button
              className={`btn ${!showPastEvents ? 'btn-success' : 'btn-outline-success'} px-4 py-2 me-2`}
              style={!showPastEvents ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : { borderColor: '#6B8E23', color: '#6B8E23' }}
              onClick={() => setShowPastEvents(false)}
            >
              <i className="fas fa-calendar-check me-2"></i>
              Upcoming Events
            </button>
            <button
              className={`btn ${showPastEvents ? 'btn-success' : 'btn-outline-success'} px-4 py-2`}
              style={showPastEvents ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23' } : { borderColor: '#6B8E23', color: '#6B8E23' }}
              onClick={() => setShowPastEvents(true)}
            >
              <i className="fas fa-history me-2"></i>
              Past Events
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card border-0 mb-4" style={{ borderTop: '3px solid #6B8E23', backgroundColor: '#f8faf5' }}>
          <div className="card-body p-3 p-md-4">
            <div className="row g-2 g-md-3">
              <div className="col-12 col-sm-6 col-md-3">
                <input
                  type="text"
                  placeholder="Search events..."
                  className="form-control"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  style={{ borderColor: '#d1d5db' }}
                />
              </div>
              <div className="col-6 col-sm-6 col-md-3">
                <select
                  className="form-select"
                  value={filters.eventType}
                  onChange={(e) => handleFilterChange('eventType', e.target.value)}
                  style={{ borderColor: '#d1d5db' }}
                >
                  <option value="">All Types</option>
                  <option value="webinar">Webinar</option>
                  <option value="workshop">Workshop</option>
                  <option value="training">Training</option>
                  <option value="conference">Conference</option>
                </select>
              </div>
              <div className="col-6 col-sm-6 col-md-3">
                <select
                  className="form-select"
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  style={{ borderColor: '#d1d5db' }}
                >
                  <option value="">All Categories</option>
                  <option value="Free">Free</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div className="col-6 col-sm-6 col-md-2">
                <select
                  className="form-select"
                  value={filters.language}
                  onChange={(e) => handleFilterChange('language', e.target.value)}
                  style={{ borderColor: '#d1d5db' }}
                >
                  <option value="">All Languages</option>
                  <option value="English">English</option>
                  <option value="French">French</option>
                  <option value="Portuguese">Portuguese</option>
                  <option value="Spanish">Spanish</option>
                  <option value="Arabic">Arabic</option>
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-2">
                <button
                  className="btn w-100"
                  style={{ borderColor: '#6B8E23', color: '#6B8E23' }}
                  onClick={() => setFilters({ search: '', eventType: '', category: '', language: '' })}
                >
                  <i className="fas fa-times me-2"></i>Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Events Grid */}
        {error && (
          <div className="alert alert-danger mb-4">
            <p className="mb-0">{error}</p>
          </div>
        )}

        {/* Loading state for past events */}
        {showPastEvents && pastLoading ? (
          <div className="border-0 p-5 text-center" style={{ borderTop: '3px solid #6B8E23' }}>
            <div className="spinner-border mb-3" role="status" style={{ color: '#6B8E23' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mb-0" style={{ color: '#4A4F4F' }}>Loading past events...</p>
          </div>
        ) : (
          <>
            {(showPastEvents ? pastEvents : events).length === 0 && !loading ? (
              <div className="border-0 p-5 text-center" style={{ borderTop: '3px solid #6B8E23', backgroundColor: '#f8faf5' }}>
                <i className="fas fa-calendar-times mb-3" style={{ fontSize: '48px', color: '#6B8E23' }}></i>
                <h3 className="h5 fw-bold mb-2" style={{ color: '#2C5234' }}>
                  {showPastEvents ? 'No past events found' : 'No events found'}
                </h3>
                <p className="mb-0" style={{ color: '#4A4F4F' }}>
                  {showPastEvents
                    ? 'Past events will appear here after they conclude.'
                    : 'Try adjusting your search or filter criteria.'}
                </p>
              </div>
            ) : (
              <>
                <div className="row g-3 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 row-cols-xl-5">
                  {(showPastEvents ? pastEvents : events).map((event) => (
                    <div key={event.id} className="col">
                      <div className="card h-100 border-0" style={{ borderTop: '3px solid #6B8E23', transition: 'transform 0.2s, box-shadow 0.2s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(107,142,35,0.15)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                        {event.image_url ? (
                          <img
                            src={event.image_url.startsWith('http') ? event.image_url : `${API_BASE}${event.image_url}`}
                            alt={event.name}
                            className="card-img-top"
                            style={{ height: '180px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div className="card-img-top d-flex align-items-center justify-content-center"
                            style={{
                              height: '180px',
                              background: 'linear-gradient(135deg, #6B8E23 0%, #4A6B18 100%)'
                            }}>
                            <i className="fas fa-calendar-alt text-white" style={{ fontSize: '40px' }}></i>
                          </div>
                        )}
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h3 className="h6 fw-bold" style={{ color: '#2C5234', fontSize: '0.95rem' }}>{event.name}</h3>
                            <span className={`badge ${event.category === 'Paid'
                              ? 'text-white'
                              : 'bg-info text-white'
                              }`} style={event.category === 'Paid' ? { backgroundColor: '#6B8E23', fontSize: '0.7rem' } : { fontSize: '0.7rem' }}>
                              {event.category}
                            </span>
                          </div>

                          <p className="text-muted mb-2" style={{ fontSize: '0.8rem', minHeight: '36px' }}>
                            {event.description ? event.description.substring(0, 80) + '...' : 'No description available'}
                          </p>

                          {/* YouTube Video Section for Past Events */}
                          {showPastEvents && event.youtube_video_id && (
                            <div className="mb-2">
                              <div className="alert p-2" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107' }}>
                                <p className="mb-2 small fw-bold" style={{ color: '#2C5234' }}>
                                  <i className="fas fa-video me-1" style={{ color: '#dc3545' }}></i>
                                  Watch it here
                                </p>
                                <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                                  <iframe
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', borderRadius: '6px' }}
                                    src={`https://www.youtube.com/embed/${event.youtube_video_id}`}
                                    title={`Video: ${event.name}`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  ></iframe>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* No video message for past events */}
                          {showPastEvents && !event.youtube_video_id && (
                            <div className="mb-2">
                              <div className="alert alert-light p-2 text-center">
                                <small className="text-muted">
                                  <i className="fas fa-video-slash me-1"></i>
                                  Recording not available
                                </small>
                              </div>
                            </div>
                          )}

                          {/* Price Section - Only for upcoming events */}
                          {showPastEvents === false && event.category === 'Paid' && (
                            <div className="mb-2 p-2 rounded" style={{ backgroundColor: '#f8faf5', fontSize: '0.75rem' }}>
                              <div className="row g-1">
                                {event.member_price && (
                                  <div className="col-6">
                                    <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>Member</small>
                                    <span className="fw-bold" style={{ color: '#6B8E23' }}>${parseFloat(event.member_price).toFixed(2)}</span>
                                  </div>
                                )}
                                {event.non_member_price && (
                                  <div className="col-6">
                                    <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>Non-Member</small>
                                    <span className="fw-bold" style={{ color: '#4A4F4F' }}>${parseFloat(event.non_member_price).toFixed(2)}</span>
                                  </div>
                                )}
                                {event.early_bird_price && (
                                  <div className="col-6">
                                    <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>Early Bird</small>
                                    <span className="fw-bold" style={{ color: '#2C5234' }}>${parseFloat(event.early_bird_price).toFixed(2)}</span>
                                  </div>
                                )}
                                {event.student_price && (
                                  <div className="col-6">
                                    <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>Student</small>
                                    <span className="fw-bold" style={{ color: '#0284c7' }}>${parseFloat(event.student_price).toFixed(2)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mb-2">
                            <div className="d-flex align-items-center text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                              <i className="fas fa-calendar me-2" style={{ color: '#6B8E23' }}></i>
                              {formatDate(event.start_date)}
                            </div>
                            <div className="d-flex align-items-center text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                              <i className="fas fa-clock me-2" style={{ color: '#6B8E23' }}></i>
                              {formatTime(event.start_date)}
                            </div>
                            <div className="d-flex align-items-center text-muted" style={{ fontSize: '0.75rem' }}>
                              <i className="fas fa-map-marker-alt me-2" style={{ color: '#6B8E23' }}></i>
                              {event.medium}
                            </div>
                          </div>

                          <div className="d-flex flex-column gap-2 mt-auto">
                            <span className="badge text-white text-center w-100" style={{ backgroundColor: '#6B8E23', fontSize: '0.75rem' }}>
                              {event.event_type}
                              {event.language && event.language !== 'English' && (
                                <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.7rem' }}>
                                  {event.language}
                                </span>
                              )}
                            </span>
                            <div className="d-flex gap-2">
                              <button
                                onClick={() => handleViewDetails(event)}
                                className="btn btn-sm flex-fill"
                                style={{ borderColor: '#6B8E23', color: '#6B8E23', border: '1px solid #6B8E23' }}
                              >
                                Details
                              </button>
                              {!showPastEvents && (
                                event.registration_link ? (
                                  <a
                                    href={event.registration_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm text-white flex-fill"
                                    style={{ backgroundColor: '#6B8E23' }}
                                  >
                                    Register
                                  </a>
                                ) : (
                                  <button
                                    onClick={() => handleRegisterClick(event)}
                                    className="btn btn-sm text-white flex-fill"
                                    style={{ backgroundColor: '#6B8E23' }}
                                  >
                                    Register
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-4 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                      <div className="text-muted small">
                        Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} events
                      </div>
                      <nav aria-label="Page navigation">
                        <ul className="pagination mb-0">
                          <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                            <button
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={pagination.page === 1}
                              className="page-link"
                              style={pagination.page !== 1 ? { color: '#6B8E23', borderColor: '#d1d5db' } : { borderColor: '#d1d5db' }}
                            >
                              <i className="fas fa-chevron-left me-1" style={{ fontSize: '0.75rem' }}></i>
                              Prev
                            </button>
                          </li>

                          {(() => {
                            const pages = [];
                            const total = pagination.totalPages;
                            const current = pagination.page;
                            const range = 2;

                            if (total <= 7) {
                              for (let i = 1; i <= total; i++) pages.push(i);
                            } else {
                              pages.push(1);
                              if (current - range > 2) pages.push('...');
                              for (let i = Math.max(2, current - range); i <= Math.min(total - 1, current + range); i++) {
                                pages.push(i);
                              }
                              if (current + range < total - 1) pages.push('...');
                              pages.push(total);
                            }

                            return pages.map((p, idx) =>
                              p === '...' ? (
                                <li key={`dots-${idx}`} className="page-item disabled">
                                  <span className="page-link" style={{ borderColor: '#d1d5db' }}>...</span>
                                </li>
                              ) : (
                                <li key={p} className={`page-item ${p === current ? 'active' : ''}`}>
                                  <button
                                    onClick={() => handlePageChange(p)}
                                    className="page-link"
                                    style={p === current
                                      ? { backgroundColor: '#6B8E23', borderColor: '#6B8E23', color: '#ffffff', fontWeight: '600' }
                                      : { color: '#6B8E23', borderColor: '#d1d5db' }
                                    }
                                  >
                                    {p}
                                  </button>
                                </li>
                              )
                            );
                          })()}

                          <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                            <button
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={pagination.page === pagination.totalPages}
                              className="page-link"
                              style={pagination.page !== pagination.totalPages ? { color: '#6B8E23', borderColor: '#d1d5db' } : { borderColor: '#d1d5db' }}
                            >
                              Next
                              <i className="fas fa-chevron-right ms-1" style={{ fontSize: '0.75rem' }}></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Event Details Modal */}
      {showEventDetails && selectedEvent && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable modal-lg">
            <div className="modal-content">
              <div className="modal-header" style={{ borderBottom: '2px solid #6B8E23' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#6B8E23' }}>
                  Event Details
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowEventDetails(false);
                    setSelectedEvent(null);
                  }}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {/* Event Image */}
                {selectedEvent.image_url && (
                  <img
                    src={selectedEvent.image_url.startsWith('http') ? selectedEvent.image_url : `${API_BASE}${selectedEvent.image_url}`}
                    alt={selectedEvent.name}
                    className="img-fluid rounded mb-4"
                    style={{ width: '100%', height: 'auto' }}
                  />
                )}

                {/* Event Title & Price */}
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h3 className="fw-bold mb-2">{selectedEvent.name}</h3>
                    <span className="badge" style={{ backgroundColor: '#6B8E23', fontSize: '0.9rem' }}>
                      {selectedEvent.event_type}
                    </span>
                  </div>
                  <div className="text-end">
                    {selectedEvent.category === 'Free' || (!selectedEvent.member_price && !selectedEvent.non_member_price && !selectedEvent.price) ? (
                      <div>
                        <span className="badge bg-success fs-5 px-3 py-2">FREE</span>
                        {selectedEvent.members_only ? (
                          <p className="small mb-0 mt-1" style={{ color: '#6B8E23' }}>Members Only</p>
                        ) : (
                          <p className="small mb-0 mt-1" style={{ color: '#4A4F4F' }}>Free for All</p>
                        )}
                      </div>
                    ) : (
                      <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="card-body p-3">
                          <h6 className="fw-bold mb-3" style={{ color: '#6B8E23' }}>Event Pricing</h6>
                          <div className="d-grid gap-2">
                            {selectedEvent.member_price && (
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Member Price:</span>
                                <span className="fw-bold" style={{ color: '#6B8E23' }}>${parseFloat(selectedEvent.member_price).toFixed(2)}</span>
                              </div>
                            )}
                            {selectedEvent.non_member_price && (
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Non-Member:</span>
                                <span className="fw-bold">${parseFloat(selectedEvent.non_member_price).toFixed(2)}</span>
                              </div>
                            )}
                            {selectedEvent.early_bird_price && (
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Early Bird:</span>
                                <span className="fw-bold text-success">${parseFloat(selectedEvent.early_bird_price).toFixed(2)}</span>
                              </div>
                            )}
                            {selectedEvent.student_price && (
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted small">Student Price:</span>
                                <span className="fw-bold text-info">${parseFloat(selectedEvent.student_price).toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                          <p className="text-muted small mb-0 mt-2 text-center">USD</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Event Details */}
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">About This Event</h5>
                  <p className="text-muted">
                    {selectedEvent.description || 'No description available.'}
                  </p>
                </div>

                {/* Speakers Section - Only for Webinars and Conferences */}
                {selectedEvent.speakers && (selectedEvent.event_type === 'webinar' || selectedEvent.event_type === 'conference') && (
                  <div className="mb-4">
                    <h5 className="fw-bold mb-3">Speaker(s)</h5>
                    <div className="alert" style={{ backgroundColor: '#f0f8f0', borderColor: '#6B8E23' }}>
                      <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                        {selectedEvent.speakers}
                      </p>
                    </div>
                  </div>
                )}

                {/* Event Info */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="fw-bold text-muted small">DATE & TIME</h6>
                      <p className="mb-0">{formatDate(selectedEvent.start_date)}</p>
                      <p className="mb-0 small text-muted">{formatTime(selectedEvent.start_date)}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="fw-bold text-muted small">LOCATION</h6>
                      <p className="mb-0">{selectedEvent.medium}</p>
                      {selectedEvent.location && <p className="mb-0 small text-muted">{selectedEvent.location}</p>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="fw-bold text-muted small">MAX PARTICIPANTS</h6>
                      <p className="mb-0">{selectedEvent.max_participants || 'Unlimited'}</p>
                    </div>
                  </div>
                </div>

                {selectedEvent.meeting_link && (
                  <div className="alert alert-info">
                    <h6 className="fw-bold mb-2">Meeting Link</h6>
                    <a href={selectedEvent.meeting_link} target="_blank" rel="noopener noreferrer" className="text-break">
                      {selectedEvent.meeting_link}
                    </a>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEventDetails(false);
                    setSelectedEvent(null);
                  }}
                >
                  Close
                </button>
                {selectedEvent.registration_link ? (
                  <a
                    href={selectedEvent.registration_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn text-white"
                    style={{ backgroundColor: '#6B8E23' }}
                  >
                    <i className="fas fa-external-link-alt me-2"></i>
                    Register Now
                  </a>
                ) : (
                  <button
                    type="button"
                    className="btn text-white"
                    style={{ backgroundColor: '#6B8E23' }}
                    onClick={handleRegisterFromDetails}
                  >
                    Register Now
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form Modal */}
      {showRegistrationForm && selectedEvent && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ borderBottom: '2px solid #6B8E23' }}>
                <h5 className="modal-title fw-bold" style={{ color: '#6B8E23' }}>
                  Event Registration
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowRegistrationForm(false);
                    setSelectedEvent(null);
                  }}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-4" style={{ backgroundColor: '#f0f8ff', borderColor: '#6B8E23' }}>
                  <h6 className="fw-bold mb-2" style={{ color: '#6B8E23' }}>Selected Event:</h6>
                  <p className="mb-1 fw-medium">{selectedEvent.name}</p>
                  <p className="mb-0 small text-muted">
                    {selectedEvent.category === 'Free' || !selectedEvent.price || selectedEvent.price === 0
                      ? 'FREE Event'
                      : `Price: $${parseFloat(selectedEvent.price).toFixed(2)}`}
                  </p>
                  <p className="mb-0 small text-muted">
                    {formatDate(selectedEvent.start_date)} at {formatTime(selectedEvent.start_date)}
                  </p>
                </div>

                <div className="text-center py-4">
                  <i className="fas fa-user-check mb-3" style={{ fontSize: '48px', color: '#6B8E23' }}></i>
                  <h5 className="fw-bold mb-3">Confirm Your Registration</h5>
                  <p className="text-muted mb-4">
                    You're about to register for <strong>{selectedEvent.name}</strong>
                    <br />
                    {selectedEvent.category === 'Free' || !selectedEvent.price || selectedEvent.price === 0
                      ? 'This is a FREE event for ARCA members.'
                      : `Payment of $${parseFloat(selectedEvent.price).toFixed(2)} will be required.`}
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <button
                      onClick={() => {
                        setShowRegistrationForm(false);
                        setSelectedEvent(null);
                      }}
                      className="btn btn-secondary"
                      disabled={registering}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={processRegistration}
                      className="btn text-white"
                      style={{ backgroundColor: '#6B8E23' }}
                      disabled={registering}
                    >
                      {registering ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Registering...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check me-2"></i>
                          Confirm Registration
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default EventsPage;